import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    OnInit,
    signal,
    viewChild,
    viewChildren
} from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { NavigationStart, Router } from '@angular/router';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { Tools } from '../../../core/utils/tools';
import { RouteDataModel } from '../../../models/core/route-data.model';
import { BasicDataService } from '../../../services/basic-data.service';
import { LayoutSettingsService } from '../../../services/layout-settings.service';
import { MetaResource } from '../../../services/resources/meta.resource';
import { RouteService } from '../../../services/route.service';
import { UserSessionService } from '../../../services/user-session.service';

@Component({
    selector: 'sl-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SidebarComponent extends BaseComponent implements OnInit {
    @HostBinding('class.sidenav-open') isSidebarOpen = true;
    @HostBinding('class.sidenav-narrow') isSidebarNarrow = false;

    @HostBinding('class.sidenav-hidden') get isSidebarHidden(): boolean {
        return this.isNetvote();
    }

    expansionPanels = viewChildren(MatExpansionPanel);
    content = viewChild.required('content', { read: ElementRef });

    menuItems: RouteDataModel[];
    currentLogo: string;
    isNetvote = signal(false);
    isScreenXs = signal(false);

    private readonly screenXs = '(max-width: 575.98px)'; // Default bootstrap breakpoint for xs screens
    private readonly screenSm = '(max-width: 767.98px)'; // Default bootstrap breakpoint for sm screens
    private readonly router = inject(Router);
    private readonly routeService = inject(RouteService);
    private readonly metaResource = inject(MetaResource);
    private readonly basicDataService = inject(BasicDataService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly userSessionService = inject(UserSessionService);
    private readonly layoutSettingsService = inject(LayoutSettingsService);

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        if (!this.isScreenXs()) {
            return;
        }

        const hasClickedOutside = event.composedPath().includes(this.content().nativeElement);

        if (hasClickedOutside && this.isSidebarOpen) {
            this.isSidebarOpen = false;
        }
    }

    ngOnInit(): void {
        this.metaResource.getLogo().subscribe(resp => (this.currentLogo = Tools.Image.getImgSrcForBase64(resp.value)));

        this.isNetvote.set(this.userSessionService.hasNetvoteRole);
        const bpSub = this.breakpointObserver.observe([this.screenXs, this.screenSm]).subscribe(r => this.updateMenuForScreenSize(r));

        this.setupMenuPermissions(this.routeService.generateMenu());
        this.setupRouteListener();
        this.loadInitialSidebarSettings();
        this.addSubscriptions(bpSub);
    }

    updateMenuForScreenSize(breakpointState: BreakpointState): void {
        if (breakpointState.matches) {
            if (breakpointState.breakpoints[this.screenSm] === true) {
                this.isSidebarNarrow = true;
                this.isScreenXs.set(false);
            }

            if (breakpointState.breakpoints[this.screenXs] === true) {
                this.isSidebarNarrow = false;
                this.isSidebarOpen = false;
                this.isScreenXs.set(true);
            }
        } else {
            this.isSidebarNarrow = false;
            this.isSidebarOpen = true;
            this.isScreenXs.set(false);
        }
    }

    toggleSidebar(): void {
        this.isSidebarOpen = !this.isSidebarOpen;
        this.updateSidebarSettings(!this.isSidebarOpen);
    }

    resizeSidebar(): void {
        this.isSidebarNarrow = !this.isSidebarNarrow;

        // if we minimize the sidebar, close all open expansion panels since we don't have space for them
        // when they're opened.
        if (this.isSidebarNarrow) {
            this.expansionPanels().forEach(e => e.close());
        }

        this.updateSidebarSettings(this.isSidebarNarrow);
    }

    openSidebar(): void {
        this.isSidebarNarrow = false;
    }

    private setupMenuPermissions(items: RouteDataModel[]): void {
        this.basicDataService
            .get()
            .subscribe(r => (this.menuItems = r?.hasActiveMeeting ? items : items.filter(i => i.title !== 'GeneralMeeting')));
    }

    private setupRouteListener(): void {
        const routerSub = this.router.events
            .pipe(
                filter(event => event instanceof NavigationStart),
                distinctUntilChanged()
            )
            .subscribe(() => (this.isSidebarOpen = false));
        this.addSubscriptions(routerSub);
    }

    private updateSidebarSettings(isCollapsed: boolean): void {
        this.layoutSettingsService.setNavigationCollapsed(isCollapsed);
    }

    private loadInitialSidebarSettings(): void {
        this.layoutSettingsService.getLayoutSettings().subscribe(currentSettings => {
            if (this.isScreenXs()) {
                return; // don't set sidebarOpen or narrow which is invalid for small screens.
            }

            this.isSidebarOpen = !currentSettings.isNavigationCollapsed;
            this.isSidebarNarrow = currentSettings.isNavigationCollapsed;
        });
    }
}
